import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';
import type { MatchStatus } from '@lib/live/match/types';

/**
 * One fixture in the engine's gameweek list.
 *
 * Like the snapshot endpoint, `GET /live/matches` forwards the live match
 * engine's payload unchanged and the public API types it as an opaque record,
 * so this is our reading of the engine's contract rather than a generated
 * type. Only the fields the harness needs are declared.
 */
export type GameweekMatchSummary = {
  matchId: string;
  status: MatchStatus;
  kickoffTime: string;
  homeTeam: { id: string; name: string; score: number };
  awayTeam: { id: string; name: string; score: number };
  /** `null` for a real Opta-derived fixture, a source id for a synthetic one. */
  provenance: string | null;
};

type GameweekMatchesPayload = {
  gameweekId: number;
  matches: GameweekMatchSummary[];
};

export const isSimulatedFixture = (fixture: GameweekMatchSummary): boolean =>
  fixture.provenance !== null;

/**
 * Every fixture the engine holds for a gameweek, simulated ones included.
 *
 * A simulated fixture is created alongside the real one it stands in for and
 * copies its `gameweekId`, so this list carries both. That is what makes it
 * the right source for the harness's fixture picker: the simulated match never
 * reaches web any other way, because a squad's `optaMatchIds` come from FFH's
 * own fixture table, which holds no simulated ids at all.
 */
export const fetchGameweekMatches = async (
  gameweek: number,
  accessToken: string
): Promise<GameweekMatchSummary[]> => {
  const { data, error, response } = await ffhClient().GET('/live/matches', {
    params: { query: { gameweek } },
    headers: authHeader(accessToken)
  });

  if (error || !data) {
    throw new Error(
      `Gameweek matches fetch failed: ${response?.status ?? 'no response'}`
    );
  }

  const payload = data.data as unknown as GameweekMatchesPayload;
  return payload?.matches ?? [];
};
